import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { useSessionById } from "../../api/SessionsAPI";
import { useSessionResponses } from "../../api/SessionResponsesAPI";
import {
  useCompetenciesByIds,
  useAllCompetencies,
} from "../../api/CompetenciesAPI";
import Breakdown from "../../components/reports/Breakdown";
import Distribution from "../../components/reports/Distribution";
import PhaseGrid from "../../components/reports/PhaseGrid";
import CinemaMapPDF from "../../components/reports/CinemaMapPDF";
import { PhaseDefs } from "../../constants/definitions";
import ReportWeb from "../../components/reports/ReportWeb";
import { Card } from "antd";

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Poppins",
  },
  header: {
    marginBottom: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: 800,
    marginBottom: 10,
  },
  sessionDetailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  leftDetails: {
    fontSize: 8,
  },
  rightDetails: {
    fontSize: 8,
    textAlign: "right",
  },
  detailLine: {
    marginBottom: 3,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    marginTop: 4,
    marginBottom: 2,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
  },
});

const PHASES = [
  "Empathize",
  "Define",
  "Ideate",
  "Prototype",
  "Test",
  "Communicate",
];

// Map category names to chart keys
const categoryMap = {
  skills: "skills",
  knowledge: "knowledge",
  mindset: "mindset",
  tool: "instruments",
};

// Helper function to generate chart images from data
const generateChartImages = async (data) => {
  console.log("generateChartImages called with data:", data);
  
  if (!data) {
    console.log("No data provided to generateChartImages");
    return {};
  }

  try {
    // Dynamically import Chart.js
    const ChartModule = await import('chart.js/auto');
    const Chart = ChartModule.Chart || ChartModule.default;
    console.log("Chart.js imported successfully");
    
    const chartImages = {};
    
    // Process data to get phase-based breakdown
    const phaseData = {};
    
    PHASES.forEach(phase => {
      phaseData[phase] = {
        skills: 0,
        knowledge: 0,
        mindset: 0,
        instruments: 0,
        total: 0
      };
    });

    Object.entries(data).forEach(([category, phases]) => {
      Object.entries(phases).forEach(([phase, percentage]) => {
        if (phase !== 'total' && phaseData[phase]) {
          const totalInCategory = phases.total || 0;
          const count = Math.round((percentage / 100) * totalInCategory) || 0;
          phaseData[phase][category] = count;
          phaseData[phase].total += count;
        }
      });
    });

    console.log("Phase data calculated:", phaseData);

    // Create charts for each phase
    for (const phase of PHASES) {
      console.log(`Creating chart for phase: ${phase}`);
      const data = phaseData[phase];
      const categories = ['skills', 'knowledge', 'mindset', 'instruments'];
      
      // Create a temporary canvas
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 300;
      
      const ctx = canvas.getContext('2d');
      
      const categoryColors = {
        skills: '#4CAF50',
        knowledge: '#2196F3',
        mindset: '#FF9800',
        instruments: '#9C27B0',
      };
      
      const chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: categories.map(cat => cat.charAt(0).toUpperCase() + cat.slice(1)),
          datasets: [{
            label: 'Competencies',
            data: categories.map(cat => data[cat]),
            backgroundColor: categories.map(cat => categoryColors[cat]),
            borderColor: categories.map(cat => categoryColors[cat]),
            borderWidth: 1,
          }]
        },
        options: {
          responsive: false,
          animation: false, // Disable animation for faster rendering
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false, // Disable for rendering
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                font: {
                  family: 'Poppins, sans-serif',
                  size: 14,
                  weight: 'bold',
                }
              },
              grid: {
                color: '#e0e0e0',
              }
            },
            x: {
              ticks: {
                font: {
                  family: 'Poppins, sans-serif',
                  size: 14,
                  weight: 'bold',
                }
              },
              grid: {
                display: false,
              }
            }
          }
        }
      });

      // Wait for chart to render
      await new Promise(resolve => setTimeout(resolve, 100));

      // Convert to image
      const imageData = canvas.toDataURL('image/png');
      chartImages[phase] = imageData;
      console.log(`Chart for ${phase} created, image data length:`, imageData.length);
      
      // Cleanup
      chart.destroy();
    }

    console.log("All charts generated, total:", Object.keys(chartImages).length);
    return chartImages;
  } catch (error) {
    console.error("Error in generateChartImages:", error);
    return {};
  }
};

const CompetencyReport = () => {
  // Get all possible parameter names
  const params = useParams();
  // Use either sessionId or id parameter, whichever is available
  const sessionId = params.sessionId || params.id;

  console.log("Report component params:", params);
  console.log("Using session ID:", sessionId);

  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useSessionById(sessionId);

  const { data: sessionResponses, isLoading: responsesLoading } =
    useSessionResponses(sessionId);

  // Get competency IDs from session responses
  const competencyIds =
    sessionResponses?.map((response) => response.competency_id) || [];

  // Fetch competency details using the IDs
  const { data: competencies, isLoading: competenciesLoading } =
    useCompetenciesByIds(competencyIds);

  // Fetch all competencies for the cinema map
  const { data: allCompetenciesData } = useAllCompetencies();

  const [breakdownData, setBreakdownData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Log data when it changes
  useEffect(() => {
    if (sessionResponses) {
      console.log("Session Responses:", sessionResponses);
      console.log("Extracted Competency IDs:", competencyIds);
    }
  }, [sessionResponses, competencyIds]);

  useEffect(() => {
    if (competencies) {
      console.log("Fetched Competencies:", competencies);

      // Group competencies by category and phase
      const groupedByCategoryAndPhase = competencies.reduce((acc, comp) => {
        const category =
          comp.category === "tool"
            ? "instruments"
            : comp.category || "uncategorized";
        const phase = comp.abilities?.phase || "uncategorized";

        if (!acc[category]) {
          acc[category] = {
            phases: {
              Empathize: 0,
              Define: 0,
              Ideate: 0,
              Prototype: 0,
              Test: 0,
              Communicate: 0,
            },
            total: 0,
          };
        }

        acc[category].phases[phase]++;
        acc[category].total++;

        return acc;
      }, {});

      // Calculate percentages for breakdown
      const categoryPhasePercentages = {};

      Object.entries(groupedByCategoryAndPhase).forEach(([category, data]) => {
        // For breakdown
        categoryPhasePercentages[category] = {
          total: data.total,
          Empathize:
            Math.round((data.phases.Empathize / data.total) * 100) || 0,
          Define: Math.round((data.phases.Define / data.total) * 100) || 0,
          Ideate: Math.round((data.phases.Ideate / data.total) * 100) || 0,
          Prototype:
            Math.round((data.phases.Prototype / data.total) * 100) || 0,
          Test: Math.round((data.phases.Test / data.total) * 100) || 0,
          Communicate:
            Math.round((data.phases.Communicate / data.total) * 100) || 0,
        };
      });

      // Add console.log to debug the data
      console.log("Grouped Data:", groupedByCategoryAndPhase);
      console.log("Breakdown Data:", categoryPhasePercentages);

      setBreakdownData(categoryPhasePercentages);
    }
  }, [competencies]);

  // Group competencies by phase and then by category within each ability
  const groupedByPhase = React.useMemo(() => {
    if (!competencies) return {};

    return PHASES.reduce((acc, phase) => {
      // Initialize each phase
      acc[phase] = {
        abilities: {},
      };

      // Filter competencies for this phase
      const phaseCompetencies = competencies.filter(
        (comp) => comp.abilities?.phase === phase
      );

      // Group by ability
      phaseCompetencies.forEach((comp) => {
        const abilityName = comp.abilities?.name || "Uncategorized";

        // Initialize the ability object if it doesn't exist
        if (!acc[phase].abilities[abilityName]) {
          acc[phase].abilities[abilityName] = {
            skills: [],
            knowledge: [],
            mindset: [],
            tool: [],
          };
        }

        // Add competency to appropriate category
        if (comp.category) {
          // Make sure the array exists
          if (!acc[phase].abilities[abilityName][comp.category]) {
            acc[phase].abilities[abilityName][comp.category] = [];
          }
          acc[phase].abilities[abilityName][comp.category].push(comp.name);
        }
      });

      return acc;
    }, {});
  }, [competencies]);

  // Add console.log to debug
  useEffect(() => {
    if (groupedByPhase) {
      console.log("Grouped by Phase:", groupedByPhase);
    }
  }, [groupedByPhase]);

  if (sessionError) {
    return (
      <div className="p-6">Error loading session: {sessionError.message}</div>
    );
  }

  return (
    <div className="h-full p-6 flex flex-col gap-6">
      
      <Card className="mb-4 bg-white text-primary rounded-lg">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <h1 className="font-semibold text-lg m-0">Session Report</h1>
            <button
              className="bg-primary hover:bg-primaryLight text-white text-xs px-3 py-1.5 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isGenerating}
              onClick={async () => {
                try {
                  setIsGenerating(true);
                  console.log("=== Starting PDF Generation ===");
                  console.log("Breakdown data:", breakdownData);
                  
                  // Generate chart images first
                  console.log("Step 1: Generating chart images...");
                  const chartImages = await generateChartImages(breakdownData);
                  console.log("Chart images generated:", Object.keys(chartImages));
                  console.log("Number of charts:", Object.keys(chartImages).length);
                  
                  // Check if we have valid image data
                  if (Object.keys(chartImages).length === 0) {
                    console.error("No chart images were generated!");
                    alert("Failed to generate charts. Check console for details.");
                    setIsGenerating(false);
                    return;
                  }
              
                  console.log("Step 2: Creating PDF document...");
                  const doc = (
                    <Document>
                      <Page size="A4" style={styles.page}>
                        <View style={styles.header}>
                          <Text style={styles.title}>
                            Competency Mapping Report
                          </Text>

                          <View style={styles.sessionDetailsContainer}>
                            <View style={styles.leftDetails}>
                              <Text style={styles.detailLine}>
                                Session ID: {session?.id}
                              </Text>
                              <Text style={styles.detailLine}>
                                Started At:{" "}
                                {formatDate(
                                  session?.created_at || session?.createdAt
                                )}
                              </Text>
                              {session?.cohort_id && (
                                <Text style={styles.detailLine}>
                                  Cohort ID: {session.cohort_id}
                                </Text>
                              )}
                            </View>

                            <View style={styles.rightDetails}>
                              <Text style={styles.detailLine}>
                                Total Competencies: {competencies?.length || 0}
                              </Text>
                              <Text style={styles.detailLine}>
                                Status: {session?.status || "in_progress"}
                              </Text>
                            </View>
                          </View>

                          <View style={styles.divider} />
                        </View>

                        <View>
                          <Text style={styles.sectionHeader}>The Big Picture</Text>
                          <CinemaMapPDF
                            allCompetenciesData={allCompetenciesData}
                            sessionResponses={sessionResponses}
                          />
                        </View>
                      </Page>

                      <Page size="A4" style={styles.page}>
                        <View style={styles.header}>
                          <Text style={styles.title}>The Breakdown</Text>
                          <View style={styles.divider} />
                        </View>

                        <Breakdown data={breakdownData} />
                      </Page>

                      <Page size="A4" style={styles.page}>
                        <View style={styles.header}>
                          <Text style={styles.title}>The Distribution</Text>
                          <View style={styles.divider} />
                        </View>

                        <Distribution chartImages={chartImages} />
                      </Page>

                      <Page size="A4" style={styles.page}>
                        <View style={styles.header}>
                          <Text style={styles.title}>The Details</Text>
                          <View style={styles.divider} />
                        </View>

                        {PHASES.map((phase) => (
                          <PhaseGrid
                            key={phase}
                            phase={phase}
                            description={PhaseDefs[phase.toUpperCase()]}
                            data={groupedByPhase[phase]?.abilities || {}}
                          />
                        ))}
                      </Page>
                    </Document>
                  );

                  console.log("Step 3: Generating PDF blob...");
                  const blob = await pdf(doc).toBlob();
                  console.log("PDF blob created, size:", blob.size);
                  
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `Session-Report-${session?.id || "Unknown"}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  console.log("=== PDF Generated Successfully! ===");
                } catch (error) {
                  console.error("=== Error generating PDF ===");
                  console.error("Error:", error);
                  console.error("Stack:", error.stack);
                  alert(`Error generating PDF: ${error.message}`);
                } finally {
                  setIsGenerating(false);
                }
              }}
            >
              {isGenerating ? "Generating..." : "Download PDF"}
            </button>
          </div>
          <p className="text-gray-500 m-0">
            <span className="font-bold">The Big Picture:</span> A visual representation of the overview of your selected competencies. <br />
            <span className="font-bold">The Breakdown:</span> A summary the distribution of competencies across design thinking phases for each category. 
            The percentages represent what portion of competencies in each category were selected for each phase.<br />
            <span className="font-bold">The Distribution:</span> Shows the number of competencies in each category for each design thinking phase with proper axes and bar charts.<br />  
            <span className="font-bold">The Details:</span> A detailed breakdown of the competencies you have chosen in each phase.
          </p>
        </div>
      </Card>
      <ReportWeb />
    </div>
  );
};

export default CompetencyReport;